/* global SideDrawer, debounce */

if (!customElements.get('facet-filters')) {
  // Adding up page counts costs one request per page, so stop asking past this.
  const COUNT_PAGE_LIMIT = 6;

  class FacetFilters extends SideDrawer {
    constructor() {
      super();
      this.initialized = false;
    }

    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.init();
    }

    init() {
      this.filteringEnabled = this.dataset.filtering === 'true';
      this.sortingEnabled = this.dataset.sorting === 'true';
      this.form = document.getElementById('facets');
      this.results = document.getElementById('filter-results');
      this.expanded = [];
      this.filterChangeTimeout = null;

      this.addElements();
      this.addListeners();
      this.refreshAvailabilityCounts().then(() => {
        this.syncHeadingCount();
        this.syncShowResultsToAvailabilityBadge();
        this.refreshMultiPageFilteredCount();
      });

      this.historyChangeHandler = this.historyChangeHandler || this.handleHistoryChange.bind(this);
      window.addEventListener('popstate', this.historyChangeHandler);
    }

    disconnectedCallback() {
      window.removeEventListener('popstate', this.historyChangeHandler);
    }

    addElements() {
      if (this.filteringEnabled || this.sortingEnabled) {
        this.filters = this.querySelector('.facets__filters');
        this.filterToggle = document.querySelector('.products-toolbar .js-toggle-filters');
      }

      if (this.filteringEnabled) {
        this.activeFilters = this.querySelector('.facets__active-filters');
        this.activeFiltersList = this.querySelector('.active-filters');
        this.resetFilters = this.querySelector('.facets__footer .js-clear-all');
        this.resultCount = document.querySelector('.products-toolbar .products-toolbar__count') || document.querySelector('.search-bar .js-result-count') || document.querySelector('.js-result-count');
        this.footer = this.querySelector('.facets__footer');
      }

      if (this.sortingEnabled) {
        this.sortByOptions = this.querySelectorAll('.js-drawer-sort-by');
      }
    }

    addListeners() {
      if (this.filteringEnabled || this.sortingEnabled) {
        this.filters.addEventListener('click', this.handleFiltersClick.bind(this));
        this.filters.addEventListener('input', debounce(this.handleFilterChange.bind(this), 300));
        this.filters.addEventListener('change', this.handleFilterChange.bind(this));
      }

      if (this.filteringEnabled) {
        this.activeFilters.addEventListener('click', this.handleActiveFiltersClick.bind(this));
        this.resetFilters.addEventListener('click', this.handleResetFilters.bind(this));
      }
    }

    /**
     * Handles 'input' events on the filters and 'change' events on the sort by dropdown.
     * @param {object} evt - Event object.
     */
    handleFilterChange(evt) {
      // Only allow price 'change' events
      if (evt.type === 'change' && !(evt.target.id?.includes('price-range') || evt.target.id?.includes('sort-by'))) return;

      // Dont reload when typing a price
      if (evt.target.id?.includes('price-range') && evt.constructor.name === 'InputEvent') return;

      const timeoutDelay = 500;

      clearTimeout(this.filterChangeTimeout);

      this.filterChangeTimeout = setTimeout(() => {
        const formData = new FormData(this.form);
        const searchParams = new URLSearchParams(formData);
        const emptyParams = [];

        if (this.sortingEnabled) {
          let currentSortBy = searchParams.get('sort_by');

          // Keep the mobile facets form sync'd with the desktop sort by dropdown
          if (evt.target.tagName === 'CUSTOM-SELECT') {
            this.sortByOptions.forEach((option) => {
              option.checked = option.value === evt.detail.selectedValue;
              currentSortBy = evt.detail.selectedValue;
            });
          }

          // Set the 'sort_by' parameter.
          searchParams.set('sort_by', currentSortBy);
        }

        // Get empty parameters.
        searchParams.forEach((value, key) => {
          if (!value) emptyParams.push(key);
        });

        // Remove empty parameters.
        emptyParams.forEach((key) => {
          searchParams.delete(key);
        });

        this.applyFilters(searchParams.toString(), evt);
      }, timeoutDelay);
    }

    /**
     * Handles 'click' events on the filters.
     * @param {object} evt - Event object.
     */
    handleFiltersClick(evt) {
      const { target } = evt;

      // Filter 'clear' button clicked.
      if (target.matches('.js-clear-filter')) {
        evt.preventDefault();
        this.applyFilters(new URL(evt.target.href).searchParams.toString(), evt);
      }

      // Filter 'show more' button clicked.
      if (target.matches('.js-show-more')) {
        const filter = target.closest('.filter');

        filter.querySelectorAll('li').forEach((el) => {
          if (!this.expanded.includes(filter.id) && el.classList.contains('js-hidden')) {
            el.classList.remove('js-hidden');
            el.classList.add('js-visible');
          } else if (this.expanded.includes(filter.id) && el.classList.contains('js-visible')) {
            el.classList.remove('js-visible');
            el.classList.add('js-hidden');
          }
        });

        if (!this.expanded.includes(filter.id)) {
          target.textContent = target.dataset.lessText;
          this.expanded.push(filter.id);
        } else {
          target.textContent = target.dataset.moreText;
          this.expanded.splice(this.expanded.indexOf(filter.id));
        }
      }
    }

    /**
     * Handles 'click' events on the active filters.
     * @param {object} evt - Event object.
     */
    handleActiveFiltersClick(evt) {
      if (evt.target.tagName !== 'A') return;
      evt.preventDefault();
      this.applyFilters(new URL(evt.target.href).searchParams.toString(), evt);
    }

    /**
     * Handles 'click' events on the reset filters button.
     * @param {object} evt - Event object.
     */
    handleResetFilters(evt) {
      if (evt.target.tagName !== 'A') return;
      evt.preventDefault();
      this.applyFilters(new URL(evt.target.href).searchParams.toString(), evt);
    }

    /**
     * Handles history changes (e.g. back button clicked).
     * @param {object} evt - Event object.
     */
    handleHistoryChange(evt) {
      if (evt.state !== null) {
        let searchParams = '';

        if (evt.state && evt.state.searchParams) {
          ({ searchParams } = evt.state);
        }

        this.applyFilters(searchParams, null, false);
      }
    }

    /**
     * Fetches the filtered/sorted page data and updates the current page.
     * @param {string} searchParams - Filter/sort search parameters.
     * @param {object} evt - Event object.
     * @param {boolean} [updateUrl=true] - Update url with the selected options.
     */
    async applyFilters(searchParams, evt, updateUrl = true) {
      try {
        // Preserve the current element focus
        const activeElementId = document.activeElement.id;

        // Disable infinite scrolling.
        const customPagination = document.querySelector('custom-pagination');
        if (customPagination) customPagination.dataset.pauseInfiniteScroll = 'true';

        // Set loading state.
        this.results.classList.add('is-loading');

        // Disable "Show X results" button until submission is complete.
        const closeBtn = this.querySelector('.facets__footer .js-close-drawer');
        closeBtn.ariaDisabled = 'true';
        closeBtn.classList.add('is-loading');

        // Use Section Rendering API for the request, if possible.
        let fetchUrl = `${window.location.pathname}?${searchParams}`;
        if (this.form.dataset.filterSectionId) {
          fetchUrl += `&section_id=${this.form.dataset.filterSectionId}`;
        }

        // Cancel current fetch request. (Raises an exception)
        if (this.applyFiltersFetchAbortController) {
          this.applyFiltersFetchAbortController.abort('Request changed');
        }
        this.applyFiltersFetchAbortController = new AbortController();

        // Fetch filtered products markup.
        const response = await fetch(fetchUrl, {
          method: 'GET',
          signal: this.applyFiltersFetchAbortController.signal
        });

        if (response.ok) {
          const tmpl = document.createElement('template');
          tmpl.innerHTML = await response.text();

          // Restore UI state.
          this.form.querySelectorAll('details-disclosure > details').forEach((existingFilter) => {
            const target = tmpl.content.getElementById(existingFilter.id);
            if (target) {
              target.open = existingFilter.open;
            }
          });
          tmpl.content.querySelectorAll('#facets details-disclosure > details').forEach((newFilter) => {
            if (this.expanded.includes(newFilter.id)) {
              const hiddenElements = newFilter.querySelectorAll('.js-hidden');
              hiddenElements.forEach((listItem) => {
                listItem.classList.remove('js-hidden');
                listItem.classList.add('js-visible');
              });
              if (newFilter.querySelector('.filter__more')) {
                newFilter.querySelector('.filter__more').textContent = newFilter.querySelector('.filter__more').dataset.lessText;
              }
            }
          });

          // Update the filters.
          this.form.innerHTML = tmpl.content.getElementById('facets').innerHTML;

          // Update the label of the "Show X results" button.
          const newCloseBtn = tmpl.content.querySelector('.facets__footer .js-close-drawer');
          if (newCloseBtn) {
            closeBtn.innerText = newCloseBtn.innerText;
            if (newCloseBtn.dataset.showCountOne) closeBtn.dataset.showCountOne = newCloseBtn.dataset.showCountOne;
            if (newCloseBtn.dataset.showCountOther) closeBtn.dataset.showCountOther = newCloseBtn.dataset.showCountOther;
          }

          // Update the results count, if its rendered.
          const newResultCount = tmpl.content.querySelector('.products-toolbar .products-toolbar__count') || tmpl.content.querySelector('.search-bar .js-result-count') || tmpl.content.querySelector('.js-result-count');
          if (this.resultCount && newResultCount) {
            this.resultCount.innerText = newResultCount.innerText;
          }

          // Update the filter toggle button.
          this.filterToggle.innerHTML = tmpl.content.querySelector('.products-toolbar .js-toggle-filters').innerHTML;

          // Preserve the CSS class of the results.
          const currentResultsUl = this.results.querySelector('ul');
          this.currentResultsClass = currentResultsUl ? this.results.querySelector('ul').getAttribute('class') : this.currentResultsClass;

          // Update the results.
          this.results.innerHTML = tmpl.content.getElementById('filter-results').innerHTML;

          // Set the CSS class of the results to what it was previously.
          const newResultsUl = this.results.querySelector('ul');
          if (newResultsUl && this.currentResultsClass) newResultsUl.setAttribute('class', this.currentResultsClass);

          // Swap the collection heading grid's featured tiles to match the
          // current filter/sort state. The fresh tiles are provided inside an
          // inert <template> in the re-rendered products section.
          const featuredTemplate = tmpl.content.getElementById('featured-tiles-template');
          const featuredTarget = document.getElementById('collection-featured-tiles');
          if (featuredTemplate && featuredTarget) {
            featuredTarget.innerHTML = featuredTemplate.innerHTML;
          }

          // innerHTML leaves the wrapper's own data attributes stale, and the
          // page total is needed to add up the pages.
          const newResults = tmpl.content.getElementById('filter-results');
          const filteredCount = newResults?.dataset?.filteredCount;
          if (newResults?.dataset?.totalPages) {
            this.results.dataset.totalPages = newResults.dataset.totalPages;
          }
          if (newResults?.dataset?.facetCount) {
            this.results.dataset.facetCount = newResults.dataset.facetCount;
          }

          const newAvailabilityCounts = tmpl.content.querySelector('#availability-counts');
          const liveAvailabilityCounts = document.getElementById('availability-counts');
          if (newAvailabilityCounts && liveAvailabilityCounts) {
            liveAvailabilityCounts.textContent = newAvailabilityCounts.textContent;
          }
          await this.refreshAvailabilityCounts(searchParams, tmpl.content);
          this.syncHeadingCount(searchParams, filteredCount);

          // Reinitialize re-rendered components.
          this.addElements();
          this.addListeners();
          this.syncHeadingCount(searchParams, filteredCount);
          this.syncShowResultsToAvailabilityBadge(searchParams);
          await this.refreshMultiPageFilteredCount(searchParams);

          // Reinitialize any custom pagination
          if (customPagination && customPagination.reload) customPagination.reload();

          // Update the URL.
          if (updateUrl) FacetFilters.updateURL(searchParams);

          // Enable the "Show X results" button
          closeBtn.classList.remove('is-loading');
          closeBtn.removeAttribute('aria-disabled');

          // Renable infinite scroll
          if (customPagination) customPagination.dataset.pauseInfiniteScroll = 'false';

          // Update the "Reset filters" button
          if (this.resetFilters) {
            const newResetFilters = tmpl.content.querySelector('.facets__footer .js-clear-all');
            if (newResetFilters) {
              this.resetFilters.href = newResetFilters.href;
              if (!newResetFilters.hasAttribute('hidden')) {
                this.resetFilters.removeAttribute('hidden');
              } else {
                this.resetFilters.setAttribute('hidden', '');
              }
            }
          }

          // Focus on the element with the same ID in the new HTML
          if (activeElementId) document.getElementById(activeElementId)?.focus();

          // Broadcast the update for anything else to hook into
          document.dispatchEvent(new CustomEvent('on:facet-filters:updated'), { bubbles: true });
        }
      } catch (error) {
        console.warn(error); // eslint-disable-line
      } finally {
        this.results.classList.remove('is-loading');
      }
    }

    /**
     * In-stock = Shopify's availability=1 count (has a purchasable variant).
     * Out-of-stock = the rest of the result set, matching PLP "Sold out"
     * cards (product.available == false). Do not loop-count: paginate max
     * 250 under-counts large collections like /collections/all.
     * @param {string} [searchParams]
     * @param {ParentNode} [source]
     */
    async refreshAvailabilityCounts(searchParams, source) {
      const params = new URLSearchParams(searchParams || window.location.search);
      params.delete('section_id');

      if (this.hasPriceFilter(params)) {
        this.syncHeadingCount(searchParams || window.location.search);
        return;
      }

      const availabilitySelected = params.has('filter.v.availability');
      const cacheKey = this.availabilityCountsCacheKey(params);

      if (availabilitySelected && this.availabilityCounts && this.availabilityCounts.key === cacheKey) {
        this.applyAvailabilityCountLabels(this.availabilityCounts.inStock, this.availabilityCounts.outOfStock, searchParams);
        this.syncHeadingCount(searchParams || window.location.search);
        return;
      }

      let data = null;
      if (!availabilitySelected) {
        data = this.parseAvailabilityCounts(source);
        if (!data && !source) {
          data = this.parseAvailabilityCounts(document);
        }
      }

      if (!data || data.inStock == null || data.outOfStock == null) {
        params.delete('filter.v.availability');
        params.delete('page');
        const sectionId = this.form?.dataset?.filterSectionId;
        if (!sectionId) return;

        const query = params.toString();
        const url = `${window.location.pathname}?${query}${query ? '&' : ''}section_id=${sectionId}`;

        try {
          const response = await fetch(url);
          if (!response.ok) return;
          const tmpl = document.createElement('template');
          tmpl.innerHTML = await response.text();
          data = this.parseAvailabilityCounts(tmpl.content);
        } catch (error) {
          console.warn(error); // eslint-disable-line
          return;
        }
      }

      if (!data || data.inStock == null || data.outOfStock == null) return;

      this.availabilityCounts = {
        key: cacheKey,
        inStock: data.inStock,
        outOfStock: data.outOfStock
      };
      this.applyAvailabilityCountLabels(data.inStock, data.outOfStock, searchParams);
      this.syncHeadingCount(searchParams || window.location.search);
    }

    availabilityCountsCacheKey(params) {
      const keyParams = new URLSearchParams(params);
      keyParams.delete('filter.v.availability');
      keyParams.delete('page');
      keyParams.delete('section_id');
      keyParams.sort();
      return keyParams.toString();
    }

    parseAvailabilityCounts(root) {
      if (!root) return null;
      const el = root.querySelector ? root.querySelector('#availability-counts') : null;
      if (!el && root.getElementById) {
        const byId = root.getElementById('availability-counts');
        if (!byId) return null;
        try {
          const data = JSON.parse(byId.textContent);
          if (data && typeof data.inStock === 'number' && typeof data.outOfStock === 'number') return data;
        } catch (error) {
          return null;
        }
        return null;
      }
      if (!el) return null;
      try {
        const data = JSON.parse(el.textContent);
        if (data && typeof data.inStock === 'number' && typeof data.outOfStock === 'number') return data;
      } catch (error) {
        return null;
      }
      return null;
    }

    applyAvailabilityCountLabels(inStock, outOfStock, searchParams) {
      const params = new URLSearchParams(searchParams || window.location.search);
      if (this.hasPriceFilter(params)) return;
      let selected = params.getAll('filter.v.availability');
      if (!selected.length) {
        selected = [...this.querySelectorAll('input[name="filter.v.availability"]:checked')].map((el) => el.value);
      }

      this.querySelectorAll('[data-availability-count]').forEach((el) => {
        const value = el.getAttribute('data-availability-count');
        if (selected.length === 1 && selected[0] === value) return;
        const count = value === '0' ? outOfStock : inStock;
        el.textContent = `(${count})`;
      });
      this.syncShowResultsToAvailabilityBadge(searchParams);
    }

    /**
     * True when a min or max price facet is in the query string.
     * @param {URLSearchParams} params
     * @returns {boolean}
     */
    hasPriceFilter(params) {
      return params.has('filter.v.price.gte') || params.has('filter.v.price.lte');
    }

    /**
     * Collection heading lives in a different section, so AJAX filter updates
     * must write the count here. When Out of stock (or In stock) is selected
     * without a price range, use the product-level availability totals so the
     * heading and "Show X results" button match the filter badges. With a price
     * filter, keep the filtered result count instead.
     * @param {string} [searchParams]
     * @param {string|number} [fallbackCount]
     */
    syncHeadingCount(searchParams, fallbackCount) {
      const params = new URLSearchParams(searchParams || window.location.search);
      let availability = params.getAll('filter.v.availability');
      if (!availability.length) {
        availability = [...this.querySelectorAll('input[name="filter.v.availability"]:checked')].map((el) => el.value);
      }
      const oosOnly = availability.includes('0') && !availability.includes('1');
      const inStockOnly = availability.includes('1') && !availability.includes('0');
      const priceActive = this.hasPriceFilter(params);
      const resultsEl = document.getElementById('filter-results');
      const totalPages = Number(resultsEl?.dataset.totalPages || 1);

      let count = null;
      if (!priceActive && oosOnly && this.availabilityCounts && this.availabilityCounts.outOfStock != null) {
        count = this.availabilityCounts.outOfStock;
      } else if (!priceActive && inStockOnly && this.availabilityCounts && this.availabilityCounts.inStock != null) {
        count = this.availabilityCounts.inStock;
      } else if (priceActive && totalPages > COUNT_PAGE_LIMIT) {
        count = resultsEl?.dataset.facetCount;
      } else if (fallbackCount != null && fallbackCount !== '') {
        count = fallbackCount;
      }

      if (count == null || count === '') {
        if (resultsEl && resultsEl.dataset.filteredCount !== '') {
          count = resultsEl.dataset.filteredCount;
        }
      }

      if (count == null || count === '') return;
      this.setHeadingProductCount(count);
      this.setShowResultsCount(count);
      this.setSelectedAvailabilityBadge(count);
    }

    setHeadingProductCount(count) {
      const n = Number(count);
      if (Number.isNaN(n)) return;
      const label = n === 1 ? 'product' : 'product(s)';
      document.querySelectorAll('.product-grid_prodCount__k_L9R p').forEach((el) => {
        el.textContent = `${n} ${label}`;
      });
    }

    setSelectedAvailabilityBadge(count) {
      const n = Number(count);
      if (Number.isNaN(n)) return;
      const checked = [...this.querySelectorAll('input[name="filter.v.availability"]:checked')];
      if (checked.length !== 1) return;
      const badge = this.querySelector(`[data-availability-count="${checked[0].value}"]`);
      if (badge) badge.textContent = `(${n})`;
    }

    setShowResultsCount(count) {
      const n = Number(count);
      if (Number.isNaN(n)) return;

      const closeBtn = this.querySelector('.facets__footer .js-close-drawer');
      if (closeBtn) {
        const template = n === 1 ? closeBtn.dataset.showCountOne : closeBtn.dataset.showCountOther;
        if (template) {
          closeBtn.textContent = template.replace(/\d+/, String(n));
        } else if (/\d+/.test(closeBtn.textContent)) {
          closeBtn.textContent = closeBtn.textContent.replace(/\d+/, String(n));
        }
      }

      if (this.resultCount) {
        this.resultCount.textContent = this.resultCount.textContent.replace(/\d+/, String(n));
      }

      const resultsEl = document.getElementById('filter-results');
      if (resultsEl) resultsEl.dataset.filteredCount = String(n);
    }

    /**
     * A price range has no usable facet total, because Shopify matches any
     * variant in range while the grid only keeps products you can actually buy
     * in range. The rendered count per page is right, so add the pages up.
     * @param {string} [searchParams]
     */
    async refreshMultiPageFilteredCount(searchParams) {
      const params = new URLSearchParams(searchParams || window.location.search);
      params.delete('section_id');
      params.delete('page');

      if (!this.hasPriceFilter(params)) return;

      const resultsEl = document.getElementById('filter-results');
      const totalPages = Number(resultsEl?.dataset.totalPages || 1);
      const sectionId = this.form?.dataset?.filterSectionId;
      if (!sectionId || totalPages <= 1 || totalPages > COUNT_PAGE_LIMIT) return;

      const cacheKey = params.toString();
      if (this.multiPageCount && this.multiPageCount.key === cacheKey) {
        this.setHeadingProductCount(this.multiPageCount.total);
        this.setShowResultsCount(this.multiPageCount.total);
        return;
      }

      const pages = [];
      for (let page = 1; page <= totalPages; page += 1) pages.push(page);

      try {
        const counts = await Promise.all(
          pages.map(async (page) => {
            const pageParams = new URLSearchParams(params);
            pageParams.set('page', String(page));
            pageParams.set('section_id', sectionId);

            const response = await fetch(`${window.location.pathname}?${pageParams}`);
            if (!response.ok) throw new Error(`Filter count page ${page} failed`);

            const tmpl = document.createElement('template');
            tmpl.innerHTML = await response.text();
            return Number(tmpl.content.getElementById('filter-results')?.dataset.filteredCount || 0);
          })
        );

        const total = counts.reduce((sum, count) => sum + count, 0);
        this.multiPageCount = { key: cacheKey, total };
        this.setHeadingProductCount(total);
        this.setShowResultsCount(total);
      } catch (error) {
        console.warn(error); // eslint-disable-line
      }
    }

    /**
     * Keep "Show X results" identical to the selected Availability badge.
     */
    syncShowResultsToAvailabilityBadge(searchParams) {
      if (this.hasPriceFilter(new URLSearchParams(searchParams || window.location.search))) return;
      const checked = [...this.querySelectorAll('input[name="filter.v.availability"]:checked')];
      if (checked.length !== 1) return;
      const badge = this.querySelector(`[data-availability-count="${checked[0].value}"]`);
      if (!badge) return;
      const match = badge.textContent.match(/\d+/);
      if (!match) return;
      this.setShowResultsCount(match[0]);
    }

    /**
     * Updates the url with the current filter/sort parameters.
     * @param {string} searchParams - Filter/sort parameters.
     */
    static updateURL(searchParams) {
      window.history.pushState(
        { searchParams },
        '',
        `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`
      );
    }
  }

  customElements.define('facet-filters', FacetFilters);
}
